import * as ActionCableNs from 'actioncable';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const /** @type {?} */ ActionCable = ActionCableNs;
export class Cable {
  baseCable: any;
private disconnectedSource: Subject<any> = new Subject();
/**
 * @param {?} url
 * @param {?=} params
 */
constructor(public url: string,
public params?: any) {
    this.baseCable = ActionCable.createConsumer(this.buildUrl(url, params));

    // If a function is passed as params, re-evaluate it before attempting to reconnect
    if (params instanceof Function) {
      this.disconnected().subscribe(() => {
        this.baseCable.url = ActionCable.createWebSocketURL(this.buildUrl(url, params));
      });
    }
  }
/**
 * Create a new subscription to a channel, optionally with topic parameters.
 * @param {?} name
 * @param {?=} params
 * @return {?}
 */
channel(name: string, params = {}): Channel {
    const /** @type {?} */ channel = new Channel(this, name, params);
    channel.disconnected().subscribe(data => this.disconnectedSource.next(data));
    return channel;
  }
/**
 * Emits when the WebSocket connection is closed.
 * @return {?}
 */
disconnected(): Observable<any> {
    return this.disconnectedSource.asObservable().pipe(debounceTime(100));
  }
/**
 * Close the connection.
 * @return {?}
 */
disconnect(): void {
    this.baseCable.disconnect();
  }
/**
 * @param {?} url
 * @param {?=} params
 * @return {?}
 */
protected buildUrl(url: string, params?: any): string {
    if (params instanceof Function) {
      params = params();
    }

    if (!params) {
      return url;
    }

    const /** @type {?} */ paramString = Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');

    return [url, paramString].join('?');
  }
}

function Cable_tsickle_Closure_declarations() {
/** @type {?} */
Cable.prototype.baseCable;
/** @type {?} */
Cable.prototype.disconnectedSource;
/** @type {?} */
Cable.prototype.url;
/** @type {?} */
Cable.prototype.params;
}

export class Channel {
  baseChannel: any;
  messages: Observable<any>;
private initializedSource: Subject<any> = new Subject();
private connectedSource: Subject<any> = new Subject();
private disconnectedSource: Subject<any> = new Subject();
private rejectedSource: Subject<any> = new Subject();
private eventTypes = ['initialized', 'connected', 'disconnected', 'rejected'];
/**
 * @param {?} cable
 * @param {?} name
 * @param {?=} params
 */
constructor(public cable: Cable,
public name: string,
public params = {}) {
    const channelParams = Object.assign({}, params, { channel: name });
    this.messages = new Observable((observer) => {
      const mixin = {
        received: (data: any) => observer.next(data),
      };

      this.eventTypes.forEach((type) => {
        mixin[type] = (data: any) => this[`${type}Source`].next(data);
      });

      this.baseChannel = this.cable.baseCable.subscriptions.create(channelParams, mixin);
      return () => this.unsubscribe();
    });
  }
/**
 * Emits messages that have been broadcast to the channel.
 * For easy clean-up, when this Observable is completed the ActionCable channel will also be closed.
 * @return {?}
 */
received(): Observable<any> {
    return this.messages;
  }
/**
 * Emits when the subscription is initialized.
 * @return {?}
 */
initialized(): Observable<any> {
    return this.initializedSource.asObservable();
  }
/**
 * Emits when the subscription is ready for use on the server.
 * @return {?}
 */
connected(): Observable<any> {
    return this.connectedSource.asObservable();
  }
/**
 * Emits when the WebSocket connection is closed.
 * @return {?}
 */
disconnected(): Observable<any> {
    return this.disconnectedSource.asObservable();
  }
/**
 * Emits when the subscription is rejected by the server.
 * @return {?}
 */
rejected(): Observable<any> {
    return this.rejectedSource.asObservable();
  }
/**
 * Broadcast message to other clients subscribed to this channel.
 * @param {?} data
 * @return {?}
 */
send(data: any): void {
    this.baseChannel.send(data);
  }
/**
 * Perform a channel action with the optional data passed as an attribute.
 * @param {?} action
 * @param {?=} data
 * @return {?}
 */
perform(action: string, data?: any): void {
    this.baseChannel.perform(action, data);
  }
/**
 * Unsubscribe from the channel.
 * @return {?}
 */
unsubscribe(): void {
    this.baseChannel.unsubscribe();
    this.eventTypes.forEach(type => this[`${type}Source`].complete());
  }
}

function Channel_tsickle_Closure_declarations() {
/** @type {?} */
Channel.prototype.baseChannel;
/** @type {?} */
Channel.prototype.messages;
/** @type {?} */
Channel.prototype.initializedSource;
/** @type {?} */
Channel.prototype.connectedSource;
/** @type {?} */
Channel.prototype.disconnectedSource;
/** @type {?} */
Channel.prototype.rejectedSource;
/** @type {?} */
Channel.prototype.eventTypes;
/** @type {?} */
Channel.prototype.cable;
/** @type {?} */
Channel.prototype.name;
/** @type {?} */
Channel.prototype.params;
}

